import type { HiType } from '../constants';
import { CURENTIA_ASSIGNER, DOCUMENT_TYPES, IDENTIFIER_TYPES, PROFILES, READING_CODES, SYSTEMS } from './codes';
import type {
  Bundle,
  BundleEntry,
  CodeableConcept,
  Coding,
  Composition,
  CompositionSection,
  Condition,
  FhirResource,
  Identifier,
  Medication,
  MedicationDispense,
  MedicationRequest,
  Narrative,
  Observation,
  Organization,
  Patient,
  Practitioner,
  Quantity,
  Reference,
} from './types';

export interface PatientInput {
  name: string;
  gender?: 'male' | 'female' | 'other' | 'unknown';
  /** YYYY-MM-DD */
  birthDate?: string;
  mobile?: string;
  abhaNumber?: string;
  abhaAddress?: string;
  /** The patient's Curentia account, when they have one. */
  accountId?: string;
  /** The practice's own record number for the patient. */
  medicalRecord?: { value: string; assigner: string };
}

export interface PractitionerInput {
  name: string;
  registration?: { council: string; number: string };
  hprId?: string;
  /** As printed under the name, such as "MBBS, MD (Medicine)". */
  qualifications?: string;
}

export interface OrganizationInput {
  name: string;
  practiceId?: string;
  hfrId?: string;
  phone?: string;
  /** The street address as one line or several. */
  address?: string;
  city?: string;
}

const uuid = () => globalThis.crypto.randomUUID();

const ESCAPES: Record<string, string> = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' };

/** Plain text as FHIR narrative XHTML, one paragraph per line, with every special character escaped. */
export function textNarrative(text: string): Narrative {
  const paragraphs = text
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter((line) => line.length > 0)
    .map((line) => `<p>${line.replace(/[&<>"']/g, (c) => ESCAPES[c] ?? c)}</p>`);
  return { status: 'additional', div: `<div xmlns="http://www.w3.org/1999/xhtml">${paragraphs.join('')}</div>` };
}

function accountIdentifier(accountId: string): Identifier {
  return { type: { coding: [IDENTIFIER_TYPES.patientInternal] }, value: accountId, assigner: { display: CURENTIA_ASSIGNER } };
}

const isAccountIdentifier = (identifier: Identifier) =>
  identifier.type?.coding?.some((c) => c.code === IDENTIFIER_TYPES.patientInternal.code) === true && identifier.assigner?.display === CURENTIA_ASSIGNER;

/** A copy of a Patient linked to this Curentia account, replacing any account it named before. */
export function withAccountId(patient: Patient, accountId: string): Patient {
  return { ...patient, identifier: [...(patient.identifier ?? []).filter((i) => !isAccountIdentifier(i)), accountIdentifier(accountId)] };
}

export function buildPatient(input: PatientInput): Patient {
  const identifier: Identifier[] = [];
  if (input.abhaNumber) identifier.push({ type: { coding: [IDENTIFIER_TYPES.abha] }, system: SYSTEMS.abha, value: input.abhaNumber });
  if (input.abhaAddress) {
    identifier.push({ type: { coding: [IDENTIFIER_TYPES.abha], text: 'ABHA address' }, system: SYSTEMS.abha, value: input.abhaAddress });
  }
  if (input.medicalRecord) {
    identifier.push({
      type: { coding: [IDENTIFIER_TYPES.medicalRecord] },
      value: input.medicalRecord.value,
      assigner: { display: input.medicalRecord.assigner },
    });
  }
  if (input.accountId) identifier.push(accountIdentifier(input.accountId));
  if (identifier.length === 0) throw new Error('A patient needs an ABHA ID, a record number or a Curentia account');
  return {
    resourceType: 'Patient',
    meta: { profile: [PROFILES.Patient] },
    identifier,
    name: [{ text: input.name }],
    ...(input.mobile ? { telecom: [{ system: 'phone' as const, value: input.mobile, use: 'mobile' as const }] } : {}),
    ...(input.gender ? { gender: input.gender } : {}),
    ...(input.birthDate ? { birthDate: input.birthDate } : {}),
  };
}

export function buildPractitioner(input: PractitionerInput): Practitioner {
  const identifier: Identifier[] = [];
  if (input.hprId) identifier.push({ type: { coding: [IDENTIFIER_TYPES.medicalLicense] }, system: SYSTEMS.hpr, value: input.hprId });
  if (input.registration) {
    identifier.push({
      type: { coding: [IDENTIFIER_TYPES.medicalLicense] },
      value: input.registration.number,
      assigner: { display: input.registration.council },
    });
  }
  if (identifier.length === 0) throw new Error('A practitioner needs a registration number or an HPR ID');
  const qualifications = input.qualifications?.trim();
  return {
    resourceType: 'Practitioner',
    meta: { profile: [PROFILES.Practitioner] },
    identifier,
    name: [{ text: input.name }],
    ...(qualifications ? { qualification: [{ code: { text: qualifications } }] } : {}),
  };
}

export function buildOrganization(input: OrganizationInput): Organization {
  const identifier: Identifier[] = [];
  if (input.hfrId) identifier.push({ type: { coding: [IDENTIFIER_TYPES.providerNumber] }, system: SYSTEMS.hfr, value: input.hfrId });
  if (input.practiceId) {
    identifier.push({ type: { coding: [IDENTIFIER_TYPES.organization] }, value: input.practiceId, assigner: { display: CURENTIA_ASSIGNER } });
  }
  return {
    resourceType: 'Organization',
    meta: { profile: [PROFILES.Organization] },
    ...(identifier.length > 0 ? { identifier } : {}),
    name: input.name,
    ...(input.phone ? { telecom: [{ system: 'phone' as const, value: input.phone, use: 'work' as const }] } : {}),
    ...(input.city || input.address?.trim()
      ? { address: [{ ...(input.address?.trim() ? { text: input.address.trim() } : {}), ...(input.city ? { city: input.city } : {}), country: 'IN' }] }
      : {}),
  };
}

/** Collects resources under urn:uuid full URLs and hands back references to them. */
class Entries {
  readonly list: BundleEntry[] = [];

  add(resource: FhirResource, display?: string): Reference {
    const id = uuid();
    const fullUrl = `urn:uuid:${id}`;
    this.list.push({ fullUrl, resource: { ...resource, id } });
    return { reference: fullUrl, type: resource.resourceType, ...(display ? { display } : {}) };
  }

  first(resource: FhirResource): void {
    const id = uuid();
    this.list.unshift({ fullUrl: `urn:uuid:${id}`, resource: { ...resource, id } });
  }
}

function documentBundle(hiType: HiType, date: string, entries: Entries, composition: Omit<Composition, 'resourceType' | 'meta' | 'status' | 'type' | 'date' | 'title' | 'language'>): Bundle {
  const kind = DOCUMENT_TYPES[hiType];
  entries.first({
    resourceType: 'Composition',
    meta: { versionId: '1', lastUpdated: date, profile: [kind.profile] },
    language: 'en-IN',
    status: 'final',
    type: kind.type,
    date,
    title: kind.title,
    ...composition,
  });
  const id = uuid();
  return {
    resourceType: 'Bundle',
    id,
    meta: { versionId: '1', lastUpdated: date, profile: [PROFILES.DocumentBundle] },
    identifier: { system: SYSTEMS.uri, value: `urn:uuid:${id}` },
    type: 'document',
    timestamp: date,
    entry: entries.list,
  };
}

export interface MedicineInput {
  name: string;
  /** A SNOMED CT code when the medicine list has one. */
  code?: Coding;
  /** The whole instruction as the doctor wants it read, such as "1 tablet, 1-0-1, after food". */
  dosage: string;
  timesPerDay?: number;
  durationDays?: number;
  instructions?: string;
}

export interface PrescriptionInput {
  /** ISO date and time the prescription was written. */
  date: string;
  patient: PatientInput;
  doctor: PractitionerInput;
  practice: OrganizationInput;
  diagnoses?: { text: string; code?: Coding }[];
  medicines: MedicineInput[];
  /** Other text shown with the prescription. */
  notes?: string;
  /** Advice to the patient, such as diet or rest. */
  advice?: string;
  /** When to come back: a YYYY-MM-DD date, a note, or both. */
  followUp?: { date?: string; note?: string };
  /** The pharmacy the doctor sends it to, recorded as the intended dispenser. */
  dispenser?: { name: string; practiceId?: string };
}

const concept = (text: string, code?: Coding): CodeableConcept => (code ? { coding: [code], text } : { text });

/** How advice and follow-up lines start in a prescription's narrative, so readers can find them again. */
export const ADVICE_PREFIX = 'Advice: ';
export const FOLLOW_UP_PREFIX = 'Follow-up: ';

const lines = (text: string | undefined) => (text ?? '').split(/\r?\n/).map((line) => line.trim()).filter((line) => line.length > 0);

/**
 * The narrative under the medicines: other notes, then the advice with its
 * first line marked, then one follow-up line with its date first.
 */
function prescriptionNarrative(input: Pick<PrescriptionInput, 'notes' | 'advice' | 'followUp'>): string {
  const advice = lines(input.advice);
  const followUp = [input.followUp?.date?.trim(), lines(input.followUp?.note).join(' ')].filter(Boolean).join('. ');
  return [
    ...lines(input.notes),
    ...advice.map((line, i) => (i === 0 ? `${ADVICE_PREFIX}${line}` : line)),
    ...(followUp ? [`${FOLLOW_UP_PREFIX}${followUp}`] : []),
  ].join('\n');
}

/**
 * An ABDM PrescriptionRecord document: Composition, Patient, Practitioner,
 * Organization, a Condition per diagnosis and a MedicationRequest per medicine.
 */
export function buildPrescriptionRecord(input: PrescriptionInput): Bundle {
  if (input.medicines.length === 0) throw new Error('A prescription needs at least one medicine');
  const entries = new Entries();
  const patient = entries.add(buildPatient(input.patient), input.patient.name);
  const doctor = entries.add(buildPractitioner(input.doctor), input.doctor.name);
  const practice = entries.add(buildOrganization(input.practice), input.practice.name);
  // The intended pharmacy is named by its Curentia ID rather than added to the document.
  const dispenser: Reference | undefined = input.dispenser
    ? {
        type: 'Organization',
        ...(input.dispenser.practiceId
          ? { identifier: { type: { coding: [IDENTIFIER_TYPES.organization] }, value: input.dispenser.practiceId, assigner: { display: CURENTIA_ASSIGNER } } }
          : {}),
        display: input.dispenser.name,
      }
    : undefined;
  const reasons = (input.diagnoses ?? []).map((diagnosis) =>
    entries.add({
      resourceType: 'Condition',
      meta: { profile: [PROFILES.Condition] },
      clinicalStatus: { coding: [{ system: SYSTEMS.conditionClinical, code: 'active', display: 'Active' }] },
      category: [{ coding: [{ system: SYSTEMS.conditionCategory, code: 'encounter-diagnosis', display: 'Encounter Diagnosis' }] }],
      code: concept(diagnosis.text, diagnosis.code),
      subject: patient,
      recordedDate: input.date,
    } satisfies Condition),
  );
  const requests = input.medicines.map((medicine) => {
    const repeat = {
      ...(medicine.timesPerDay ? { frequency: medicine.timesPerDay, period: 1, periodUnit: 'd' as const } : {}),
      ...(medicine.durationDays
        ? { boundsDuration: { value: medicine.durationDays, unit: 'days', system: SYSTEMS.ucum, code: 'd' } }
        : {}),
    };
    return entries.add({
      resourceType: 'MedicationRequest',
      meta: { profile: [PROFILES.MedicationRequest] },
      status: 'active',
      intent: 'order',
      medicationCodeableConcept: concept(medicine.name, medicine.code),
      subject: patient,
      authoredOn: input.date,
      requester: doctor,
      ...(reasons.length > 0 ? { reasonReference: reasons } : {}),
      dosageInstruction: [
        {
          text: medicine.dosage,
          ...(medicine.instructions ? { additionalInstruction: [{ text: medicine.instructions }] } : {}),
          ...(Object.keys(repeat).length > 0 ? { timing: { repeat } } : {}),
        },
      ],
      ...(dispenser ? { dispenseRequest: { performer: dispenser } } : {}),
    } satisfies MedicationRequest, medicine.name);
  });
  const type = DOCUMENT_TYPES.Prescription.type;
  const narrative = prescriptionNarrative(input);
  const section: CompositionSection = {
    title: 'Prescription record',
    code: { coding: type.coding ?? [] },
    entry: requests,
    ...(narrative ? { text: textNarrative(narrative) } : {}),
  };
  return documentBundle('Prescription', input.date, entries, {
    subject: patient,
    author: [doctor],
    custodian: practice,
    section: [section],
  });
}

/** A reading a patient or doctor records. Times are ISO date and time. */
export type Reading =
  | { kind: 'blood_pressure'; at: string; systolic: number; diastolic: number }
  | { kind: 'pulse'; at: string; bpm: number }
  | { kind: 'spo2'; at: string; percent: number }
  | { kind: 'temperature'; at: string; celsius: number }
  | { kind: 'weight'; at: string; kg: number }
  | { kind: 'blood_glucose'; at: string; mgPerDl: number; context?: 'fasting' | 'after_meal' | 'random' };

export const GLUCOSE_CONTEXT_TEXT = {
  fasting: 'Blood glucose, fasting',
  after_meal: 'Blood glucose, after a meal',
  random: 'Blood glucose, random',
} as const;

const ucum = (value: number, unit: string, code: string): Quantity => ({ value, unit, system: SYSTEMS.ucum, code });
const category = (code: 'vital-signs' | 'laboratory', display: string) => [
  { coding: [{ system: SYSTEMS.observationCategory, code, display }] },
];

function observation(reading: Reading, subject: Reference, performer: Reference): { section: string; resource: Observation } {
  const base = { resourceType: 'Observation' as const, status: 'final' as const, subject, effectiveDateTime: reading.at, performer: [performer] };
  const vital = { meta: { profile: [PROFILES.ObservationVitalSigns] }, category: category('vital-signs', 'Vital Signs') };
  switch (reading.kind) {
    case 'blood_pressure':
      return {
        section: 'Vital Signs',
        resource: {
          ...base,
          ...vital,
          code: { coding: [READING_CODES.bloodPressure], text: 'Blood pressure' },
          component: [
            { code: { coding: [READING_CODES.systolic] }, valueQuantity: ucum(reading.systolic, 'mmHg', 'mm[Hg]') },
            { code: { coding: [READING_CODES.diastolic] }, valueQuantity: ucum(reading.diastolic, 'mmHg', 'mm[Hg]') },
          ],
        },
      };
    case 'pulse':
      return { section: 'Vital Signs', resource: { ...base, ...vital, code: { coding: [READING_CODES.pulse], text: 'Pulse' }, valueQuantity: ucum(reading.bpm, 'beats/minute', '/min') } };
    case 'spo2':
      return {
        section: 'Vital Signs',
        resource: { ...base, ...vital, code: { coding: [READING_CODES.spo2, READING_CODES.spo2PulseOximetry], text: 'SpO2' }, valueQuantity: ucum(reading.percent, '%', '%') },
      };
    case 'temperature':
      return { section: 'Vital Signs', resource: { ...base, ...vital, code: { coding: [READING_CODES.temperature], text: 'Temperature' }, valueQuantity: ucum(reading.celsius, '°C', 'Cel') } };
    case 'weight':
      return {
        section: 'Body Measurement',
        resource: {
          ...base,
          meta: { profile: [PROFILES.ObservationBodyMeasurement] },
          category: category('vital-signs', 'Vital Signs'),
          code: { coding: [READING_CODES.weight], text: 'Weight' },
          valueQuantity: ucum(reading.kg, 'kg', 'kg'),
        },
      };
    case 'blood_glucose':
      return {
        section: 'Other Observations',
        resource: {
          ...base,
          meta: { profile: [PROFILES.Observation] },
          category: category('laboratory', 'Laboratory'),
          code: { coding: [READING_CODES.bloodGlucose], text: GLUCOSE_CONTEXT_TEXT[reading.context ?? 'random'] },
          valueQuantity: ucum(reading.mgPerDl, 'mg/dL', 'mg/dL'),
        },
      };
  }
}

export interface WellnessInput {
  date: string;
  patient: PatientInput;
  readings: Reading[];
  /** Set when a doctor records the readings. Otherwise the patient is the author. */
  recordedBy?: PractitionerInput;
}

const WELLNESS_SECTIONS = ['Vital Signs', 'Body Measurement', 'Other Observations'] as const;

/** An ABDM WellnessRecord document holding readings in the guide's sections. */
export function buildWellnessRecord(input: WellnessInput): Bundle {
  if (input.readings.length === 0) throw new Error('A wellness record needs at least one reading');
  const entries = new Entries();
  const patient = entries.add(buildPatient(input.patient), input.patient.name);
  const author = input.recordedBy ? entries.add(buildPractitioner(input.recordedBy), input.recordedBy.name) : patient;
  const bySection = new Map<string, Reference[]>();
  for (const reading of input.readings) {
    const { section, resource } = observation(reading, patient, author);
    bySection.set(section, [...(bySection.get(section) ?? []), entries.add(resource)]);
  }
  const section = WELLNESS_SECTIONS.filter((title) => bySection.has(title)).map((title) => ({
    title,
    entry: bySection.get(title) ?? [],
  }));
  return documentBundle('WellnessRecord', input.date, entries, { subject: patient, author: [author], section });
}

export interface DispensedItem {
  /** What was handed over: the medicine prescribed, or the substitute given for it. */
  medicine: string;
  code?: Coding;
  /** The prescription's MedicationRequest ID this item answers. */
  requestId?: string;
  quantity?: { value: number; unit: string };
  /** The batch handed over, and its expiry as YYYY-MM. */
  batch?: { number: string; expiry?: string };
  /** completed when handed over, declined when not dispensed. */
  status: 'completed' | 'declined';
  substitution?: { reason: string };
  note?: string;
}

export interface DispenseInput {
  date: string;
  patient: PatientInput;
  pharmacist: PractitionerInput;
  pharmacy: OrganizationInput;
  /** The identifier value of the prescription document being dispensed. */
  prescriptionId: string;
  items: DispensedItem[];
}

/**
 * A dispensing record: a collection bundle with one MedicationDispense per
 * item. ABDM has no dispensing profile, so these follow base FHIR R4.
 */
export function buildDispenseRecord(input: DispenseInput): Bundle {
  if (input.items.length === 0) throw new Error('A dispensing record needs at least one item');
  const entries = new Entries();
  const patient = entries.add(buildPatient(input.patient), input.patient.name);
  const pharmacist = entries.add(buildPractitioner(input.pharmacist), input.pharmacist.name);
  entries.add(buildOrganization(input.pharmacy), input.pharmacy.name);
  for (const item of input.items) {
    const prescription: Reference = item.requestId
      ? { identifier: { system: SYSTEMS.uri, value: `urn:uuid:${item.requestId}` } }
      : { identifier: { system: SYSTEMS.uri, value: input.prescriptionId } };
    // A known batch makes the medicine a Medication of its own, which is where FHIR keeps batches.
    const medication = item.batch
      ? {
          medicationReference: entries.add(
            {
              resourceType: 'Medication',
              code: concept(item.medicine, item.code),
              batch: { lotNumber: item.batch.number, ...(item.batch.expiry ? { expirationDate: item.batch.expiry } : {}) },
            } satisfies Medication,
            item.medicine,
          ),
        }
      : { medicationCodeableConcept: concept(item.medicine, item.code) };
    entries.add({
      resourceType: 'MedicationDispense',
      status: item.status,
      ...medication,
      subject: patient,
      performer: [{ actor: pharmacist }],
      authorizingPrescription: [prescription],
      ...(item.quantity ? { quantity: { value: item.quantity.value, unit: item.quantity.unit } } : {}),
      ...(item.status === 'completed' ? { whenHandedOver: input.date } : {}),
      ...(item.substitution
        ? {
            substitution: {
              wasSubstituted: true,
              type: { coding: [{ system: SYSTEMS.substitution, code: 'E', display: 'equivalent' }] },
              reason: [{ text: item.substitution.reason }],
            },
          }
        : {}),
      ...(item.note ? { note: [{ text: item.note }] } : {}),
    } satisfies MedicationDispense, item.medicine);
  }
  const id = uuid();
  return {
    resourceType: 'Bundle',
    id,
    meta: { versionId: '1', lastUpdated: input.date },
    identifier: { system: SYSTEMS.uri, value: `urn:uuid:${id}` },
    type: 'collection',
    timestamp: input.date,
    entry: entries.list,
  };
}
